'use client'

import { HomeContainer } from '@/components/common/home/home-container'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { CSSProperties, useEffect, useRef } from 'react'
import { JOURNEY_ITEMS } from './constants'

type JourneyItem = {
  key: string
  image?: string
  imageStyle?: {
    [key: string]: string | number | undefined
  }
  yellow?: boolean
  green?: boolean
  red?: boolean
  pink?: boolean
  images?: {
    src: string
    alt: string
    style: {
      [key: string]: string | number | undefined
    }
  }[]
  gridImages?: {
    src: string
    alt: string
    style: {
      [key: string]: string | number | undefined
    }
  }[]
  imageContainerStyle?: {
    [key: string]: string | number | undefined
  }
}

const getItemColorClasses = (item: JourneyItem) => {
  if (item.yellow)
    return {
      bg: 'bg-[#f9f96c]',
      text: 'text-neutral-900',
      dot: 'before:bg-[#f9f96c]',
    }
  if (item.green)
    return {
      bg: 'bg-[#3ace6d]',
      text: 'text-light',
      dot: 'before:bg-[#3ace6d]',
    }
  if (item.red)
    return {
      bg: 'bg-[#f44f4f]',
      text: 'text-light',
      dot: 'before:bg-[#f44f4f]',
    }
  if (item.pink)
    return {
      bg: 'bg-[#FFC0CB]',
      text: 'text-neutral-900',
      dot: 'before:bg-[#FFC0CB]',
    }
  return {
    bg: 'bg-[#2f5acf]',
    text: 'text-light',
    dot: 'before:bg-[#2f5acf]',
  }
}

const TimelineDot = ({ colorClass }: { colorClass: string }) => (
  <div
    className={cn(
      'relative mb-5 mt-8 h-0.5 w-full bg-neutral-900',
      'before:absolute before:left-0 before:top-1/2 before:z-[1] before:h-5 before:w-5 before:-translate-y-1/2',
      'before:rounded-full before:border-2 before:border-neutral-900',
      colorClass,
      'after:absolute after:-right-[60px] after:top-0 after:h-full after:w-1/2 after:bg-neutral-900'
    )}
  />
)

const TimelineItem = ({ item }: { item: JourneyItem }) => {
  const t = useTranslations()
  const { bg, text, dot } = getItemColorClasses(item)

  return (
    <li className='min-w-fit pr-15'>
      <p
        className={cn(
          'mb-2.5 font-sans text-lg font-semibold leading-5 text-primary',
          item.yellow && 'text-neutral-900',
          item.green && 'text-[#3ace6d]',
          item.red && 'text-[#f44f4f]',
          item.pink && 'text-neutral-900'
        )}
      >
        {t(`landing_coolmate_101_page.journey.items.${item.key}.date` as never)}
      </p>

      <div className='h-28'>
        <div
          className={cn(
            'relative w-fit min-w-[256px] rounded-md border-2 border-neutral-900 p-2 px-4 shadow-[4px_4px_0_0_#000]',
            bg,
            text
          )}
          dangerouslySetInnerHTML={{
            __html: t.raw(
              `landing_coolmate_101_page.journey.items.${item.key}.content` as never
            ),
          }}
        />
      </div>

      <div
        className={cn(
          'relative mb-5 mt-8 h-0.5 w-full bg-neutral-900',
          'before:absolute before:left-0 before:top-1/2 before:z-[1] before:h-5 before:w-5 before:-translate-y-1/2 before:rounded-full before:border-2 before:border-neutral-900',
          dot,
          'after:absolute after:-right-[60px] after:top-0 after:h-full after:w-1/2 after:bg-neutral-900'
        )}
      />

      {item.image && (
        <Image
          src={ImageHelper.getImageUrl(item.image)}
          alt={
            t(
              `landing_coolmate_101_page.journey.items.${item.key}.image_alt` as never
            ) || ''
          }
          width={150}
          height={132}
          className='max-h-[132px] w-auto rounded-[10px] object-cover'
          style={item.imageStyle as CSSProperties}
        />
      )}
    </li>
  )
}

export const JourneySection = () => {
  const t = useTranslations()
  const scrollRef = useRef<HTMLLIElement>(null)
  const containerRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    if (scrollRef.current && containerRef.current) {
      const container = containerRef.current
      const item = scrollRef.current
      const scrollPos =
        item.offsetLeft - container.clientWidth / 2 + item.clientWidth / 2
      container.scrollTo({
        left: scrollPos,
        behavior: 'smooth',
      })
    }
  }, [])

  return (
    <HomeContainer className='py-10 lg:pb-20 lg:pt-24'>
      <h2 className='mb-4 font-criteria text-2xl font-semibold uppercase leading-tight text-neutral-900 lg:text-4xl'>
        {t('landing_coolmate_101_page.journey.title')}
      </h2>
      <p className='mb-12 font-sans text-lg leading-6 text-neutral-900/70'>
        {t('landing_coolmate_101_page.journey.description')}
      </p>
      <ul className='flex overflow-auto' ref={containerRef}>
        {JOURNEY_ITEMS.map((item, index) => (
          <TimelineItem item={item} key={index} />
        ))}

        {/* Item "Chúng tôi đang viết tiếp" */}
        <li className='min-w-fit pr-15' ref={scrollRef}>
          <p className='invisible mb-2.5 font-sans text-lg font-bold leading-5 text-primary'>
            placeholder
          </p>
          <div className='flex h-28 flex-col justify-end'>
            <p className='font-sans text-xl font-bold leading-5 text-neutral-900'>
              {t('landing_coolmate_101_page.journey.writing_next')}
            </p>
          </div>
          <TimelineDot colorClass='before:bg-light' />
        </li>

        {/* Future Item - IPO */}
        <li className='min-w-fit pr-15'>
          <p className='mb-2.5 font-sans text-lg font-semibold leading-5 text-[#7b61ff]'>
            Trước 2030
          </p>
          <div className='h-28'>
            <div className='relative w-fit min-w-[256px] rounded-md border-2 border-neutral-900 bg-[#7b61ff] p-2 px-4 text-light shadow-[4px_4px_0_0_#000]'>
              {t('landing_coolmate_101_page.journey.ipo')}
            </div>
          </div>
          <div className='relative mb-5 mt-8 h-0.5 w-full border-b-2 border-dashed border-neutral-900 bg-transparent before:absolute before:left-0 before:top-1/2 before:z-[1] before:h-5 before:w-5 before:-translate-y-1/2 before:rounded-full before:border-2 before:border-neutral-900 before:bg-[#7b61ff] after:absolute after:-right-[60px] after:top-0 after:h-full after:w-1/2 after:bg-neutral-900'>
            <span className='absolute right-[-3%] top-1/2 h-3 w-3 -translate-y-1/2 rotate-45 border-r-2 border-t-2 border-neutral-900'></span>
          </div>
        </li>
      </ul>
    </HomeContainer>
  )
}
